import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MessageCircle, ArrowRight, Loader2 } from "lucide-react";
import Avatar from "@/components/Avatar";

/**
 * Mini corner feed — shows the last N messages of the corner_group for a given store.
 * Renders a compact card. Clickable → jumps to /journal and (future: opens the chat).
 */
export default function CornerFeed({ storeId, storeName, limit = 3 }) {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!storeId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    api.get(`/stores/${storeId}/corner-feed`, { params: { limit } })
      .then(({ data }) => setRows(data?.messages || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [storeId, limit]);

  if (!storeId) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`corner-feed-${storeId}`}>
      <div className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center gap-2">
        <MessageCircle className="w-4 h-4"/>
        <div className="font-display font-bold text-sm">Fil corner {storeName || ""}</div>
        <button onClick={() => nav("/journal")} data-testid={`corner-feed-open-${storeId}`}
                className="ml-auto text-[11px] font-bold px-2 py-0.5 rounded bg-white/20 hover:bg-white/30 flex items-center gap-1">
          Ouvrir <ArrowRight className="w-3 h-3"/>
        </button>
      </div>
      <div className="divide-y divide-slate-100">
        {loading && <div className="p-4 text-center text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline"/></div>}
        {!loading && rows.length === 0 && <div className="p-4 text-xs text-slate-400 italic text-center">Aucun message récent dans ce corner.</div>}
        {rows.map(m => {
          const isRoy = m.is_bot || m.sender_id === "roy-bot";
          return (
            <div key={m.id} className={`p-3 flex gap-2 ${isRoy ? "bg-amber-50/50" : ""}`}>
              <Avatar user={{ id: m.sender_id, name: m.sender_name, avatar_url: m.sender_avatar }} size={26}/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className={`text-xs font-bold truncate ${isRoy ? "text-amber-700" : "text-slate-800"}`}>{m.sender_name}</div>
                  <div className="text-[10px] text-slate-400 shrink-0">{new Date(m.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
                <div className="text-xs text-slate-700 whitespace-pre-wrap line-clamp-3">{m.body}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
